import { Component } from '@theme/component';

const THE_LANE_TRANSIENT_RECOMMENDATION_STATUSES = new Set([429, 500, 502, 503, 504]);
const THE_LANE_RECOMMENDATION_RETRY_DELAY = 250;

class ProductRecommendations extends Component {
  /**
   * The observer for the product recommendations
   * @type {IntersectionObserver}
   */
  #intersectionObserver = new IntersectionObserver(
    (entries, observer) => {
      if (!entries[0]?.isIntersecting) return;

      observer.disconnect();
      this.#loadRecommendations();
    },
    { rootMargin: '0px 0px 400px 0px' }
  );

  /**
   * Observing changes to the elements attributes
   * @type {MutationObserver}
   */
  #mutationObserver = new MutationObserver((mutations) => {
    for (const mutation of mutations) {
      // Only attribute changes are interesting
      if (mutation.target !== this || mutation.type !== 'attributes') continue;

      // Ignore error attribute changes
      if (mutation.attributeName === 'data-error') continue;

      // Ignore addition of hidden class because it means there's an error with the display
      if (mutation.attributeName === 'class' && this.classList.contains('hidden')) continue;

      // Ignore when the data-recommendations-performed attribute has been set to 'true'
      if (
        mutation.attributeName === 'data-recommendations-performed' &&
        this.dataset.recommendationsPerformed === 'true'
      )
        continue;

      // All other attribute changes trigger a reload
      this.#loadRecommendations();
      break;
    }
  });

  /**
   * The cached recommendations
   * @type {Record<string, string>}
   */
  #cachedRecommendations = {};

  /**
   * An abort controller for the active fetch (if there is one)
   * @type {AbortController | null}
   */
  #activeFetch = null;

  connectedCallback() {
    super.connectedCallback();
    this.#intersectionObserver.observe(this);
    this.#mutationObserver.observe(this, { attributes: true });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#intersectionObserver.disconnect();
    this.#mutationObserver.disconnect();
  }

  /**
   * Load the product recommendations
   */
  #loadRecommendations() {
    const { productId, recommendationsPerformed, sectionId, intent } = this.dataset;
    const id = this.id;

    if (!productId || !id) {
      throw new Error('Product ID and an ID attribute are required');
    }

    // If the recommendations have already been loaded, accounts for the case where the Theme Editor
    // is loaded the section from the editor's visual preview context.
    if (recommendationsPerformed === 'true') {
      return;
    }

    this.#fetchCachedRecommendations(productId, sectionId, intent)
      .then((result) => {
        if (!result.success) {
          // The Theme Editor will place a section element element in the DOM whose section_id is not available
          // to the Section Renderer API. In this case, we can safely ignore the error.
          if (!Shopify.designMode) {
            this.#handleError(new Error(`Server returned ${result.status}`));
          }
          return;
        }

        const html = document.createElement('div');
        html.innerHTML = result.data || '';
        const recommendations = html.querySelector(`product-recommendations[id="${id}"]`);

        if (recommendations?.innerHTML && recommendations.innerHTML.trim().length) {
          this.dataset.recommendationsPerformed = 'true';
          this.innerHTML = recommendations.innerHTML;
        } else {
          this.#handleError(new Error('No recommendations available'));
        }
      })
      .catch((e) => {
        this.#handleError(e);
      });
  }

  /**
   * Fetches the recommendations and cached the result for future use
   * @param {string} productId
   * @param {string | undefined} sectionId
   * @param {string | undefined} intent
   * @returns {Promise<{ success: true, data: string } | { success: false, status: number }>}
   */
  async #fetchCachedRecommendations(productId, sectionId, intent) {
    const requestUrl = new URL(this.dataset.url, window.location.origin);
    requestUrl.searchParams.set('product_id', productId);
    if (sectionId) requestUrl.searchParams.set('section_id', sectionId);
    if (intent) requestUrl.searchParams.set('intent', intent);
    const url = requestUrl.toString();

    const cachedResponse = this.#cachedRecommendations[url];
    if (cachedResponse) {
      return { success: true, data: cachedResponse };
    }

    this.#activeFetch?.abort();
    const controller = new AbortController();
    this.#activeFetch = controller;

    try {
      let response = await fetch(url, { signal: controller.signal });
      if (!response.ok && THE_LANE_TRANSIENT_RECOMMENDATION_STATUSES.has(response.status)) {
        await new Promise((resolve) => setTimeout(resolve, THE_LANE_RECOMMENDATION_RETRY_DELAY));
        response = await fetch(url, { signal: controller.signal });
      }

      if (!response.ok) {
        return { success: false, status: response.status };
      }

      const text = await response.text();
      this.#cachedRecommendations[url] = text;
      return { success: true, data: text };
    } finally {
      if (this.#activeFetch === controller) this.#activeFetch = null;
    }
  }

  /**
   * Handle errors in a consistent way
   * @param {Error} error
   */
  #handleError(error) {
    console.error('Product recommendations error:', error.message);
    this.classList.add('hidden');
    this.dataset.error = 'Error loading product recommendations';
  }
}

if (!customElements.get('product-recommendations')) {
  customElements.define('product-recommendations', ProductRecommendations);
}

const THE_LANE_UK_RECOMMENDATION_LIMIT = 4;
const theLaneBoundRecommendationRoots = new WeakSet();

/**
 * Return all UK ranges found in merchant-entered copy.
 * @param {unknown} value
 * @returns {Array<{min: number, max: number}>}
 */
function theLaneExtractUkRanges(value) {
  const text = String(value || '').replace(/[—–]/g, '-');
  const ranges = [];
  const expression = /\bUK\s*(\d{1,2})(?:\s*(?:-|to)\s*(\d{1,2}))?/gi;
  let match;

  while ((match = expression.exec(text))) {
    let min = Number.parseInt(match[1], 10);
    let max = match[2] ? Number.parseInt(match[2], 10) : min;
    const prefix = text.slice(Math.max(0, match.index - 80), match.index).trim();

    if (/(up\s*to|under|below|maximum|max\.?)(?:\s+\w+){0,3}\s*$/i.test(prefix)) {
      min = 6;
    }

    ranges.push({ min: Math.min(min, max), max: Math.max(min, max) });
  }

  return ranges;
}

/**
 * @param {unknown} value
 * @returns {string}
 */
function theLaneNormalizeSize(value) {
  if (window.TheLaneUkFit?.normalizeSize) return window.TheLaneUkFit.normalizeSize(value);

  const normalized = String(value || '')
    .replace(/\(?\s*UK\s*\d{1,2}\s*(?:[-–—]|to)\s*\d{1,2}\s*\)?/gi, '')
    .replace(/\(?\s*UK\s*\d{1,2}\s*\)?/gi, '')
    .replace(/length\s*:?\s*\d+(?:\.\d+)?\s*(?:cm|inches?)?/gi, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '');

  if (normalized === 'xxl') return '2xl';
  if (normalized === 'xxxl') return '3xl';
  if (normalized.startsWith('onesize')) return 'onesize';
  return normalized;
}

/**
 * Parse both supported size-matrix formats.
 * @param {unknown} value
 * @returns {Array<{size: string, ukMin: number|null, ukMax: number|null}>}
 */
function theLaneParseRecommendationMatrix(value) {
  if (window.TheLaneUkFit?.parseMatrix) return window.TheLaneUkFit.parseMatrix(value);

  return String(value || '')
    .replace(/\r/g, '')
    .split('\n')
    .map((line) => line.trim())
    .filter(Boolean)
    .map((line) => {
      const parts = line.split(/[｜|]/).map((part) => part.trim());
      const possibleMin = Number.parseFloat(parts[2]);
      const possibleMax = Number.parseFloat(parts[3]);
      const isNewFormat =
        parts.length >= 10 &&
        possibleMin >= 4 &&
        possibleMin <= 40 &&
        possibleMax >= 4 &&
        possibleMax <= 40;

      if (isNewFormat) {
        return {
          size: parts[1] || '',
          ukMin: Math.min(possibleMin, possibleMax),
          ukMax: Math.max(possibleMin, possibleMax),
        };
      }

      const ranges = theLaneExtractUkRanges(parts[1] || parts[0]);
      return {
        size: parts[1] || parts[0] || '',
        ukMin: ranges[0]?.min ?? null,
        ukMax: ranges[0]?.max ?? null,
      };
    });
}

/**
 * @param {HTMLScriptElement|null} metadataNode
 * @param {number} ukSize
 * @returns {boolean}
 */
function theLaneCandidateMatches(metadataNode, ukSize) {
  if (!metadataNode) return false;

  let data;
  try {
    data = JSON.parse(metadataNode.textContent || '{}');
  } catch {
    return false;
  }

  const options = Array.isArray(data.options) ? data.options : [];
  const variants = Array.isArray(data.variants) ? data.variants : [];
  const sizeIndex = options.findIndex((option) => String(option).trim().toLowerCase() === 'size');
  const availableVariants = variants.filter((variant) => variant.available);
  const availableSizes =
    sizeIndex >= 0
      ? availableVariants.map((variant) => theLaneNormalizeSize(variant.options?.[sizeIndex])).filter(Boolean)
      : [];

  const rows = theLaneParseRecommendationMatrix(data.matrix);
  const matchingRows = rows.filter(
    (row) =>
      row.ukMin !== null &&
      row.ukMax !== null &&
      ukSize >= row.ukMin &&
      ukSize <= row.ukMax
  );

  if (matchingRows.length) {
    if (sizeIndex < 0) return availableVariants.length > 0;
    return matchingRows.some((row) => availableSizes.includes(theLaneNormalizeSize(row.size)));
  }

  if (
    sizeIndex >= 0 &&
    availableVariants.some((variant) =>
      theLaneExtractUkRanges(variant.options?.[sizeIndex]).some(
        (range) => ukSize >= range.min && ukSize <= range.max
      )
    )
  ) {
    return true;
  }

  const copyRanges = theLaneExtractUkRanges([data.fit, data.fitPoints].filter(Boolean).join(' '));
  return availableVariants.length > 0 && copyRanges.some((range) => ukSize >= range.min && ukSize <= range.max);
}

/**
 * @param {HTMLElement} root
 */
function theLaneApplyUkRecommendationFilter(root) {
  const selectedUkSize = Number.parseInt(
    document.documentElement.getAttribute('data-tl-uk-size') || '',
    10
  );
  const displayLimit =
    Number.parseInt(root.dataset.displayLimit || '', 10) || THE_LANE_UK_RECOMMENDATION_LIMIT;
  const lists = root.querySelectorAll('.resource-list');

  lists.forEach((list) => {
    const cards = Array.from(list.querySelectorAll('.resource-list__item'));
    if (!cards.length) return;

    let visibleCount = 0;
    cards.forEach((card, index) => {
      const matches =
        Number.isNaN(selectedUkSize) ||
        theLaneCandidateMatches(card.querySelector('[data-tl-fit-candidate]'), selectedUkSize);
      const shouldShow = matches && visibleCount < displayLimit;
      card.hidden = !shouldShow;
      if (shouldShow) visibleCount += 1;

      if (Number.isNaN(selectedUkSize) && index >= displayLimit) {
        card.hidden = true;
      }
    });

    let status = list.parentElement?.querySelector(':scope > [data-tl-uk-recommendation-status]');
    if (!status) {
      status = document.createElement('p');
      status.setAttribute('data-tl-uk-recommendation-status', 'true');
      status.setAttribute('aria-live', 'polite');
      status.style.margin = '0 0 20px';
      status.style.color = 'var(--color-foreground)';
      status.style.fontSize = '12px';
      status.style.opacity = '.72';
      list.parentNode?.insertBefore(status, list);
    }

    if (Number.isNaN(selectedUkSize)) {
      status.hidden = true;
      status.textContent = '';
    } else {
      status.hidden = false;
      status.textContent =
        visibleCount > 0
          ? `Showing styles with an available fit for UK ${selectedUkSize}.`
          : `No exact UK ${selectedUkSize} fit is available in the current recommendations.`;
    }
  });
}

function theLaneInitUkRecommendationFilters() {
  document.querySelectorAll('product-recommendations').forEach((root) => {
    if (theLaneBoundRecommendationRoots.has(root)) {
      theLaneApplyUkRecommendationFilter(root);
      return;
    }

    theLaneBoundRecommendationRoots.add(root);
    const observer = new MutationObserver(() => theLaneApplyUkRecommendationFilter(root));
    observer.observe(root, { childList: true });
    theLaneApplyUkRecommendationFilter(root);
  });
}

document.addEventListener('thelane:uk-size-change', theLaneInitUkRecommendationFilters);
document.addEventListener('DOMContentLoaded', theLaneInitUkRecommendationFilters);
document.addEventListener('shopify:section:load', theLaneInitUkRecommendationFilters);
window.setTimeout(theLaneInitUkRecommendationFilters, 500);
window.setTimeout(theLaneInitUkRecommendationFilters, 1500);
